import { SmartImage } from "@/components/site/SmartImage";
import { knowledgeImages } from "@/lib/responsive-images";
import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { ArrowRight } from "lucide-react";

import { CtaBand, PageHero } from "@/components/site/Page";
import { ResourceBody } from "@/components/site/ResourceGate";
import { articleJsonLd, seoLinks, ogImage, clampTitle, clampDescription } from "@/lib/seo";
import { resourceBySlug, resources } from "@/lib/resources";
import { getResourceMeta } from "@/lib/resources.functions";
import { safeLoader } from "@/lib/safe-loader";

export const Route = createFileRoute("/kunskapsbank/$slug")({
  loader: async ({ params }) => {
    const resource = resourceBySlug(params.slug);
    if (!resource || resource.slug !== params.slug) throw notFound();
    const meta = await safeLoader("sv.resource-meta", () => getResourceMeta({ data: { slug: resource.slug, locale: "sv" } }), null);
    return { slug: resource.slug, meta };
  },
  head: ({ params, loaderData }) => {
    const resource = resourceBySlug(params.slug);
    if (!resource) {
      return { meta: [{ title: "Sidan hittades inte" }, { name: "robots", content: "noindex" },
        { property: "og:locale", content: "sv_SE" },
        ...ogImage(),
      ] };
    }
    const title = clampTitle(loaderData?.meta?.title ?? resource.title, "Kunskapsbank");
    const description = clampDescription(loaderData?.meta?.description ?? resource.intro);
    const path = `/kunskapsbank/${resource.slug}`;
    return {
      meta: [
        { title },
        { name: "description", content: description },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:type", content: "article" },
        { name: "twitter:card", content: "summary_large_image" },
      ],
      links: seoLinks(path),
      scripts: [
        articleJsonLd({
          path,
           headline: loaderData?.meta?.title ?? resource.title,
          description,
          image: resource.image,
          locale: "sv",
        }),
      ],
    };
  },
  notFoundComponent: NotFoundView,
  component: Page,
});

function NotFoundView() {
  return (
    <>
      <PageHero
        eyebrow="Kunskapsbank"
        title="Materialet finns inte"
        intro="Länken verkar vara felaktig. Gå tillbaka till kunskapsbanken för att se allt material."
      />
      <div className="mx-auto max-w-3xl px-5 py-12">
        <Link to="/kunskapsbank" className="text-primary link-quiet">
          Till kunskapsbanken
        </Link>
      </div>
    </>
  );
}

function Page() {
  const { slug, meta } = Route.useLoaderData();
  const resource = resourceBySlug(slug)!;
  const others = resources.filter((r) => r.slug !== resource.slug);

  return (
    <>
      <PageHero
        eyebrow="Kunskapsbank"
        breadcrumbLabel={meta?.title ?? resource.title}
        title={meta?.title ?? resource.title}
        intro={meta?.description ?? resource.intro}
      />

      <div className="mx-auto max-w-3xl px-5 pt-10">
        <SmartImage
          image={knowledgeImages[resource.slug]!}
          alt={resource.title}
          width={1280}
          height={720}
          sizes="(max-width: 768px) 100vw, 768px"
          className="aspect-[16/9] w-full rounded-xl bg-muted object-cover"
        />
      </div>

      <ResourceBody resource={resource} locale="sv" />

      <section className="mx-auto max-w-3xl px-5 pb-12 sm:pb-16">
        <h2 className="text-lg font-semibold">Mer i kunskapsbanken</h2>
        <ul className="mt-4 space-y-3">
          {others.map((r) => (
            <li key={r.slug}>
              <Link
                to="/kunskapsbank/$slug"
                params={{ slug: r.slug }}
                className="group flex items-center justify-between gap-4 rounded-xl border border-border bg-card p-4 transition-colors hover:border-primary/40"
              >
                <span className="min-w-0">
                  <span className="block font-medium">{r.title}</span>
                  <span className="mt-1 block text-sm text-muted-foreground">
                    {r.minutes} min läsning
                  </span>
                </span>
                <ArrowRight
                  className="size-4 shrink-0 text-primary transition-transform group-hover:translate-x-0.5"
                  aria-hidden
                />
              </Link>
            </li>
          ))}
        </ul>
      </section>

      <CtaBand />
      <div className="h-16" />
    </>
  );
}
